import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, FileText, Pencil, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, errMsg } from "@/lib/api";
import { Receipt } from "@/components/messages/SeenBy";

export const msgFileUrl = (id) => `${API}/messages/files/${id}`;
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

function ImageViewer({ a, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 sm:p-10" onClick={onClose} role="dialog" aria-modal="true" data-testid="msg-image-viewer">
      <button type="button" autoFocus onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg transition-transform hover:scale-105" data-testid="msg-image-viewer-close">
        <X className="h-7 w-7" strokeWidth={2.5} />
      </button>
      <img src={msgFileUrl(a.id)} alt={a.filename} onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full rounded-lg object-contain" data-testid="msg-image-viewer-img" />
    </div>,
    document.body,
  );
}

function Attachment({ a }) {
  const [open, setOpen] = useState(false);
  if (a.content_type?.startsWith("image/")) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} className="block" data-testid={`msg-attachment-${a.id}`}>
          <img src={msgFileUrl(a.id)} alt={a.filename} className="max-h-48 cursor-zoom-in rounded-lg border border-white/40 bg-white object-contain" />
        </button>
        {open && <ImageViewer a={a} onClose={() => setOpen(false)} />}
      </>
    );
  }
  return (
    <a href={msgFileUrl(a.id)} target="_blank" rel="noreferrer" data-testid={`msg-attachment-${a.id}`}
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
      <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{a.filename}</span>
    </a>
  );
}

function EditBox({ m, mine, onDone }) {
  const [text, setText] = useState(m.body || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/messages/${m.id}`, { body: text }); toast.success("Message updated"); onDone(true); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const btn = `inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${mine ? "bg-white text-slate-900" : "bg-[hsl(var(--primary))] text-white"}`;
  return (
    <div className="space-y-2" onKeyDown={(e) => { if (e.key === "Escape") { onDone(false); } }}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} autoFocus rows={3} maxLength={5000}
        onKeyDown={(e) => { if (e.key === "Escape") onDone(false); if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } }}
        className="w-full min-w-[14rem] rounded-lg border border-slate-300 bg-white p-2 text-[15px] text-slate-900" data-testid={`msg-edit-input-${m.id}`} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => onDone(false)} className={`text-xs font-semibold underline ${mine ? "text-rose-100" : "text-slate-600"}`} data-testid={`msg-edit-cancel-${m.id}`}>Cancel</button>
        <button type="button" onClick={save} disabled={busy} className={btn} data-testid={`msg-edit-save-${m.id}`}><Check className="h-3.5 w-3.5" /> Save</button>
      </div>
    </div>
  );
}

function EditHistory({ m, mine }) {
  const [open, setOpen] = useState(false);
  const sub = mine ? "text-rose-100" : "text-slate-500";
  const label = `Edited${m.edited_by_name ? ` by ${m.edited_by_name}` : ""} · ${when(m.edited_at)}`;
  if (!m.edit_history?.length) {
    return <div className={`text-xs italic ${sub}`} title={label} data-testid={`msg-edited-${m.id}`}>Edited{m.edited_by_name ? ` by ${m.edited_by_name}` : ""}</div>;
  }
  return (
    <div className="space-y-1">
      <button type="button" onClick={() => setOpen(!open)} className={`text-xs italic underline ${sub}`} title={label} data-testid={`msg-edited-${m.id}`}>
        Edited{m.edited_by_name ? ` by ${m.edited_by_name}` : ""} · {open ? "hide" : "see"} original{m.edit_history.length > 1 ? `s (${m.edit_history.length})` : ""}
      </button>
      {open && (
        <ol className={`space-y-1 rounded-lg p-2 text-xs ${mine ? "bg-white/15 text-white" : "bg-white text-slate-700"}`} data-testid={`msg-history-${m.id}`}>
          {m.edit_history.map((h, i) => (
            <li key={i} data-testid={`msg-history-${m.id}-${i}`}>
              <span className={`block ${sub}`}>{i === 0 ? "Original" : `Version ${i + 1}`} · changed {when(h.replaced_at)}{h.replaced_by ? ` by ${h.replaced_by}` : ""}</span>
              <span className="whitespace-pre-wrap break-words">{h.body || "(no text)"}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Bubble({ m, mine, adminView, onDelete, canDeleteAll, onChanged }) {
  const [editing, setEditing] = useState(false);
  const canDelete = onDelete && (m.can_edit || canDeleteAll);
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`group max-w-[85%] space-y-2 rounded-2xl px-4 py-3 sm:max-w-[70%] ${mine ? "rounded-br-sm bg-[hsl(var(--primary))] text-white" : "rounded-bl-sm bg-[hsl(var(--sand))] text-slate-800"}`}>
        <div className={`flex items-center gap-2 text-xs ${mine ? "text-rose-100" : "text-slate-500"}`}>
          <span className="font-semibold">{m.from_admin && adminView && !/admin/i.test(m.sender_name || "") ? `${m.sender_name} · Admin` : m.sender_name}</span>
          {!m.from_admin && m.to_label && <span data-testid={`msg-to-${m.id}`}>to {m.to_label}</span>}
          <span>{when(m.created_at)}</span>
          {m.broadcast_id && <span className="inline-flex items-center gap-1" title="Sent to several people"><Users className="h-3 w-3" /> group</span>}
          {m.can_edit && !editing && <button onClick={() => setEditing(true)} className="-m-1 p-1.5 opacity-60 hover:opacity-100" data-testid={`msg-edit-${m.id}`} aria-label="Edit message"><Pencil className="h-3.5 w-3.5" /></button>}
          {canDelete && <button onClick={() => onDelete(m.id)} className="-m-1 p-1.5 opacity-60 hover:opacity-100" data-testid={`msg-delete-${m.id}`} aria-label="Delete message"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
        {editing ? <EditBox m={m} mine={mine} onDone={(changed) => { setEditing(false); if (changed) { onChanged?.(); } }} />
          : m.body && <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">{m.body}</p>}
        {m.edited_at && !editing && <EditHistory m={m} mine={mine} />}
        {m.attachments?.length > 0 && <div className="flex flex-wrap gap-2">{m.attachments.map((a) => <Attachment key={a.id} a={a} />)}</div>}
        {mine && <div className="flex justify-end text-xs text-rose-100">{m.admin_group ? <span data-testid={`msg-group-seen-${m.id}`}>{(m.read_by || []).length > 1 ? `Seen by ${(m.read_by || []).length - 1} admin${(m.read_by || []).length === 2 ? "" : "s"}` : "Sent"}</span> : <Receipt m={m} adminView={adminView} />}</div>}
      </div>
    </div>
  );
}

export function Thread({ messages, mineIsAdmin, meId, onDelete, canDeleteAll, onChanged, empty = "No messages yet." }) {
  const box = useRef(null);
  useEffect(() => { if (box.current) { box.current.scrollTop = box.current.scrollHeight; } }, [messages]);
  return (
    <div ref={box} className="h-[50vh] min-h-72 space-y-3 overflow-y-auto bg-white p-4 sm:p-5" data-testid="message-thread">
      {messages.length === 0 && <p className="py-16 text-center text-slate-500" data-testid="thread-empty">{empty}</p>}
      {messages.map((m) => <Bubble key={m.id} m={m} mine={meId ? m.sender_id === meId : m.from_admin === mineIsAdmin} adminView={mineIsAdmin} onDelete={onDelete} canDeleteAll={canDeleteAll} onChanged={onChanged} />)}
    </div>
  );
}
